/**
 * Contrato de las reglas de alerta por dispositivo (Rastreo, RR-28).
 *
 * Una regla decide si una alerta se AVISA en «Eventos de telemetría». Dos clases:
 * - `catalogo`: una alerta que Drive ya conoce (`ALERT_CATALOG`). Solo se guarda el aviso; sin
 *   fila, el aviso está activo.
 * - `personalizada`: una alerta agregada a mano para lo que el catálogo no reconoció. Se liga a un
 *   parámetro de la trama (`parametro`, p. ej. `alarm`) y al valor que la dispara (`valor`).
 *
 * Guardar una regla es una preferencia de Drive. No le manda nada al GPS (constitución, Art. 6).
 */

import { z } from "zod";

/** Código de una alerta del catálogo o identificador de una personalizada. */
export const alertCodeSchema = z.string().regex(/^[A-Za-z0-9_.:-]{1,64}$/u, "Código de alerta inválido.");

/** Nombre del atributo de la trama (`alarm`, `cellId`…): una palabra, sin espacios. */
export const alertParameterSchema = z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,31}$/u, "Parámetro inválido.");

export const MAX_ALERT_RULES_PER_DEVICE = 100;

const text = (max: number, empty: string) =>
  z
    .string()
    .transform((value) => value.trim().replace(/\s+/gu, " "))
    .pipe(z.string().min(1, empty).max(max, `Máximo ${max} caracteres.`));

export const alertRuleSchema = z.object({
  code: alertCodeSchema,
  kind: z.enum(["catalogo", "personalizada"]),
  notify: z.boolean(),
  name: z.string().nullable(),
  parameter: z.string().nullable(),
  value: z.string().nullable(),
});
export type AlertRule = z.infer<typeof alertRuleSchema>;

export const alertRulesResponseSchema = z.object({
  deviceId: z.string(),
  rules: z.array(alertRuleSchema),
});
export type AlertRulesResponse = z.infer<typeof alertRulesResponseSchema>;

/** Encender o apagar el aviso de una alerta del catálogo (o de una personalizada ya creada). */
export const setAlertNotifySchema = z.object({ notify: z.boolean() });
export type SetAlertNotifyInput = z.infer<typeof setAlertNotifySchema>;

/** Agregar una alerta: nombre, parámetro de la trama y el valor que la dispara. */
export const addAlertRuleSchema = z.object({
  name: text(60, "Escribe el nombre de la alerta."),
  parameter: alertParameterSchema,
  value: text(40, "Escribe el valor que dispara la alerta."),
});
export type AddAlertRuleInput = z.infer<typeof addAlertRuleSchema>;

/** El identificador de una personalizada: `personalizada:` y 12 caracteres hexadecimales. */
export const CUSTOM_ALERT_PREFIX = "personalizada:";
export const isCustomAlertCode = (code: string): boolean => code.startsWith(CUSTOM_ALERT_PREFIX);
